import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, FieldValues, Path, useFormContext } from 'react-hook-form';
import { TextField, TextFieldProps } from '@mui/material';
import { colorPalette } from '@qualifio/ui-commons';

export type InputTextFieldProps<FormValue> = TextFieldProps & {
  name: Path<FormValue>;
  defaultValue?: string;
  /** Debounce delay in milliseconds. When set, form updates are debounced. */
  debounceMs?: number;
};

// TODO placeholder not visible and this isn't working: https://stackoverflow.com/questions/70624192/how-to-show-both-label-and-placeholder-in-material-ui-textfield
export function InputTextField<FormValue extends FieldValues>({
  name,
  inputProps,
  defaultValue = '',
  debounceMs,
  ...textFieldProps
}: InputTextFieldProps<FormValue>) {
  const { control } = useFormContext<FormValue>();
  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue as any}
      render={({ field: { ref, onChange, value, ...field }, fieldState: { error } }) => (
        <DebouncedTextField
          {...field}
          {...textFieldProps}
          inputRef={ref}
          value={value}
          onChange={onChange}
          debounceMs={debounceMs}
          error={!!error}
          variant="outlined"
          // https://stackoverflow.com/questions/70624192/how-to-show-both-label-and-placeholder-in-material-ui-textfield
          InputLabelProps={{ shrink: true }}
          helperText={error?.message || textFieldProps.helperText}
          // TODO with next ui-commons upgrade, this will not be needed
          sx={{
            '.MuiFormLabel-root': {
              position: 'relative',
              transform: 'none',
              color: `${colorPalette.grey.dark} !important`,
            },
            // TODO make this fix in ui-commons
            // https://stackoverflow.com/questions/65161428/how-to-remove-border-in-textfield-fieldset-in-material-ui
            '.MuiOutlinedInput-notchedOutline legend': {
              width: 0,
            },
          }}
          inputProps={inputProps}
        />
      )}
    />
  );
}

type DebouncedTextFieldProps = TextFieldProps & {
  debounceMs?: number;
  onChange: (value: string) => void;
  value: string;
};

function DebouncedTextField({
  debounceMs,
  onChange,
  value,
  ...textFieldProps
}: DebouncedTextFieldProps) {
  const [localValue, setLocalValue] = useState(value ?? '');

  // Sync local value when form value changes externally
  useEffect(() => {
    setLocalValue(value ?? '');
  }, [value]);

  const debouncedOnChange = useMemo(
    () => (debounceMs ? debounce(onChange, debounceMs) : onChange),
    [onChange, debounceMs],
  );

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      if (debounceMs && 'cancel' in debouncedOnChange) {
        debouncedOnChange.cancel();
      }
    };
  }, [debouncedOnChange, debounceMs]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      setLocalValue(newValue);
      debouncedOnChange(newValue);
    },
    [debouncedOnChange],
  );

  return <TextField {...textFieldProps} value={localValue} onChange={handleChange} />;
}
